import React, { useState, useRef, useEffect } from "react";
import { Button } from "reactstrap";
import { Icon } from "../../components/Component";

interface OtpInputScreenProps {
  onVerify: (otp: string) => void;
  onResend: () => void;
  onBack?: () => void;
  phoneNumber?: string;
  loading?: boolean;
}

const OtpInputScreen: React.FC<OtpInputScreenProps> = ({
  onVerify,
  onResend,
  onBack,
  phoneNumber,
  loading = false,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendTimer, setResendTimer] = useState(300); // 5 minutes in seconds
  const [canResend, setCanResend] = useState(false);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length === 6) {
      onVerify(otpString);
    }
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleResendClick = () => {
    if (canResend) {
      setResendTimer(300);
      setCanResend(false);
      onResend();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <form onSubmit={handleSubmit} className="content clearfix">
      <div className="form-group">
        <div className="form-label-group">
          <label className="form-label">Enter OTP</label>
        </div>
        <div className="form-control-wrap">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ gap: "12px", marginBottom: "24px" }}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="form-control form-control-lg text-center"
                style={{
                  width: "56px",
                  height: "64px",
                  fontSize: "28px",
                  fontWeight: "600",
                  pointerEvents: "auto",
                  border: "1px solid #e5e9f2",
                  borderRadius: "8px",
                  padding: "0",
                }}
                disabled={loading}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="form-group mt-4">
        <Button
          type="submit"
          color="primary"
          size="lg"
          className="btn-block w-100"
          disabled={loading || otp.join("").length !== 6}
          style={{ minHeight: "48px" }}
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </Button>
      </div>

      {onBack && (
        <div className="form-group mt-3">
          <Button
            type="button"
            className="btn-block w-100"
            color="primary"
            size="lg"
            onClick={onBack}
            disabled={loading}
            style={{
              pointerEvents: "auto",
              minHeight: "48px",
            }}
          >
            Back
          </Button>
        </div>
      )}

      <div className="form-group mt-3 text-center">
        {canResend ? (
          <button
            type="button"
            onClick={handleResendClick}
            className="btn btn-link"
            disabled={loading}
            style={{ pointerEvents: "auto", textDecoration: "none" }}
          >
            Resend OTP
          </button>
        ) : (
          <div style={{ pointerEvents: "auto" }}>
            <span className="text-soft">
              Resend OTP in{" "}
              <strong className="text-primary">
                {formatTime(resendTimer)}
              </strong>
            </span>
          </div>
        )}
      </div>
    </form>
  );
};

export default OtpInputScreen;
